import React, { useState } from 'react';
import { Modal, Button, Alert, Spinner } from 'react-bootstrap';
import { useMutation, useQueryClient } from 'react-query';
import { deleteFaq, deleteFile, deleteTextBlock, deleteWebLink } from '../../api/agent';
import { FaExclamationTriangle, FaTrashAlt, FaFile, FaLink, FaQuestionCircle, FaFileAlt } from 'react-icons/fa';

type KnowledgeItemType = 'faq' | 'file' | 'textBlock' | 'webLink';

type Props = {
    show: boolean;
    onClose: () => void;
    orgId: string;
    agentId: string;
    itemId: string;
    itemType: KnowledgeItemType;
    itemName: string;
}

const ConfirmDeleteKnowledgeItem = ({ show, onClose, orgId, agentId, itemId, itemType, itemName }: Props) => {
    const [error, setError] = useState<string | null>(null);

    // Define icons for each type
    const typeIcons = {
        faq: FaQuestionCircle,
        file: FaFile,
        textBlock: FaFileAlt,
        webLink: FaLink
    };

    // Define colors for each type
    const typeColors = {
        faq: '#ffc107',
        file: '#28a745',
        textBlock: '#6f42c1',
        webLink: '#17a2b8'
    };

    // Define type labels
    const typeLabels = {
        faq: 'FAQ',
        file: 'File',
        textBlock: 'Text Block',
        webLink: 'Web Link'
    };

    const queryClient = useQueryClient();
    const { mutateAsync: deleteFAQFn, isLoading: isLoadingFaq } = useMutation(
        deleteFaq, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['Faqs', agentId]);
                onClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );
    const { mutateAsync: deleteFileFn, isLoading: isLoadingFile } = useMutation(
        deleteFile, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['Files', agentId]);
                onClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );
    const { mutateAsync: deleteTextBlockFn, isLoading: isLoadingTextBlock } = useMutation(
        deleteTextBlock, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['TextBlocks', agentId]);
                onClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );
    const { mutateAsync: deleteWebLinkFn, isLoading: isLoadingWebLink } = useMutation(
        deleteWebLink, // mutation function goes here
        {
            onSuccess: () => {
                queryClient.invalidateQueries(['Organizations']);
                queryClient.invalidateQueries(['WebLinks', agentId]);
                onClose();
            },
            onError: (error) => {
                setError("Unexpected error. Please try again or contact admin.");
            },
        }
    );

    const handleDelete = async () => {
        if (!orgId || !agentId || !itemId) {
            setError("Missing required information. Please try again.");
            return;
        }

        try {
            if (itemType === 'faq') {
                await deleteFAQFn({ orgId, agentId, id: itemId });
            } else if (itemType === 'file') {
                await deleteFileFn({ orgId, agentId, id: itemId });
            } else if (itemType === 'textBlock') {
                await deleteTextBlockFn({ orgId, agentId, id: itemId });
            } else if (itemType === 'webLink') {
                await deleteWebLinkFn({ orgId, agentId, id: itemId });
            }
        } catch (err) {
            // Error handling is done in the mutation's onError callback
        }
    };

    const IconComponent = typeIcons[itemType];
    const iconColor = typeColors[itemType];
    const typeLabel = typeLabels[itemType];

    return (
        <Modal 
            show={show} 
            onHide={onClose} 
            centered
            contentClassName="bg-dark text-light"
        >
            <Modal.Header 
                closeButton 
                className="border-0 pb-0"
                style={{ backgroundColor: '#2d2d2d', borderBottom: '1px solid #444' }}
            >
                <Modal.Title className="d-flex align-items-center text-danger">
                    <FaExclamationTriangle className="me-2" />
                    Confirm {typeLabel} Deletion
                </Modal.Title>
            </Modal.Header>
            
            <Modal.Body className="pt-2" style={{ backgroundColor: '#2d2d2d' }}>
                <div className="d-flex align-items-start">
                    <div className="bg-danger bg-opacity-20 rounded p-3 me-3 mt-1">
                        <FaTrashAlt className="text-light" size={24} />
                    </div>
                    <div className="flex-grow-1">
                        <h6 className="mb-2 text-light">Are you absolutely sure?</h6>
                        <p style={{ color: '#ccc' }} className="mb-2">
                            This action will permanently delete the {typeLabel.toLowerCase()} <strong className="text-light">"{itemName}"</strong> and all associated data.
                        </p>
                        <div 
                            className="border border-danger rounded p-3 mt-3"
                            style={{ backgroundColor: '#2d1a1a', borderColor: '#dc3545' }}
                        >
                            <div className="d-flex align-items-center mb-2">
                                <div 
                                    className="me-3 d-flex align-items-center justify-content-center"
                                    style={{
                                        width: "32px",
                                        height: "32px",
                                        backgroundColor: iconColor,
                                        borderRadius: "6px"
                                    }}
                                >
                                    <IconComponent className="text-white" size={16} />
                                </div>
                                <div>
                                    <h6 className="mb-0 text-light">{itemName}</h6>
                                    <small style={{ color: '#ccc' }}>{typeLabel}</small>
                                </div>
                            </div>
                            <small className="text-danger">
                                <strong>Warning:</strong> This action cannot be undone. All training data associated with this {typeLabel.toLowerCase()} will be permanently removed.
                            </small>
                        </div>
                        
                        {error && (
                            <Alert 
                                variant="danger" 
                                className="mt-3 mb-0"
                                style={{ 
                                    backgroundColor: '#2d1a1a',
                                    borderColor: '#dc3545',
                                    color: '#ff6b6b'
                                }}
                            >
                                <div className="d-flex align-items-center">
                                    <FaExclamationTriangle className="me-2" />
                                    {error}
                                </div>
                            </Alert>
                        )}
                    </div>
                </div>
            </Modal.Body>
            
            <Modal.Footer 
                className="border-0 pt-0"
                style={{ backgroundColor: '#2d2d2d' }}
            >
                <Button 
                    variant="outline-secondary" 
                    onClick={onClose}
                    disabled={isLoadingFaq || isLoadingFile || isLoadingTextBlock || isLoadingWebLink}
                    style={{ borderColor: '#555', color: '#ccc' }}
                >
                    Cancel
                </Button>
                <Button 
                    variant="danger" 
                    onClick={handleDelete}
                    disabled={isLoadingFaq || isLoadingFile || isLoadingTextBlock || isLoadingWebLink}
                    className="d-flex align-items-center"
                >
                    {(isLoadingFaq || isLoadingFile || isLoadingTextBlock || isLoadingWebLink) ? (
                        <>
                            <Spinner size="sm" className="me-2" />
                            Deleting...
                        </>
                    ) : (
                        <>
                            <FaTrashAlt className="me-2" />
                            Delete {typeLabel}
                        </>
                    )}
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default ConfirmDeleteKnowledgeItem;